import { debounce, throttle } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
    AppStateType,
    DisplayUeopt,
    ListType,
    SettingsType,
} from '../../../constants/enums'
import { GridDensityUeopt } from '../../../mfcLogic/ueopts'
import {
    subscribeWorkerListUpdates,
    unsubscribeWorkerListUpdates,
} from '../../../state/pubSub/workerSubscriptions'
import {
    resetCenteredIndexState,
    resetPreviewOnScrollIndexState,
    resetScrollCursorState,
    setCenteredIndexState,
    setPreviewOnScrollIndexState,
    setScrollCursorTargetState,
    startMouseMomentumTracking,
    stopMouseMomentumTracking,
} from '../../../state/state.grid'
import { isTouchDevice } from '../../../utilities/browser'
import { useAppState } from '../../Hooks/useAppState'
import { useList } from '../../Hooks/useList'
import { useModelsLoadedAppState } from '../../Hooks/useModelsLoadedAppState'
import { usePassive } from '../../Hooks/usePassive'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSettings } from '../../Hooks/useSettings'
import APP_BREAKPOINTS from '../../styles/breakpoints'

// shifts the preview trigger point below the top of the viewport; smaller
// offsets near the top of the page keep the first rows reachable
const getPreviewTriggerOffset = (scrollOffset: number) => {
    if (scrollOffset < 20) {
        return 10
    }

    if (scrollOffset < 40) {
        return 30
    }

    if (scrollOffset < 60) {
        return 50
    }

    if (scrollOffset < 80) {
        return 70
    }

    return 100
}

// the scroll cursor follows every scroll event, so keep its cadence just
// under the velocity sampling (120ms) — fast enough to feel attached to the
// scroll, coarse enough that a fling doesn't queue a step per frame
const SCROLL_CURSOR_THROTTLE_MS = 50

type ScrollTile = { row: number | null; column: number | null }

export const useHomeData = () => {
    const models = useList(ListType.DEFAULT, { passive: false })
    const noModelsVisible = models.length === 0
    // false until the first CAMS table has been ingested; while a seeded
    // page is showing, getRows pads the viewport with placeholder tiles
    const modelsLoaded = useModelsLoadedAppState()
    const { isIOS, isIpad, isMobile } = useAppState(AppStateType.APP)

    const [settings] = useSettings(SettingsType.DEFAULT)
    const {
        pinRecentlyVisitedRooms,
        pinModelsWithNotes,
        pinTrendingRooms,
        pinPopularRooms,
        pinNewModels,
        pinMissMFC,
        gridDensity,
        display,
        enableAvatarScrollPreview,
        // pinPrivateGroupShows,
        pinModelsYouMayLike,
        // pinCustomSelection,
        // pinPastWinners,
        sort,
    } = settings
    const previewScrollPreviousTimeRef = useRef(0)
    const previewScrollDisabledRef = useRef(false)
    const previewScrollPreviousRef = useRef(0)
    const previewScrollEnabled =
        display === DisplayUeopt.default &&
        isMobile &&
        enableAvatarScrollPreview !== false

    const canEnableAvatarHoverPreview =
        display === DisplayUeopt.default && !isTouchDevice

    // momentum lives in module state (state.grid) — tiles read it at event
    // time, so a velocity flip never re-renders the grid
    useEffect(() => {
        if (!canEnableAvatarHoverPreview) {
            return
        }

        startMouseMomentumTracking()

        return () => {
            stopMouseMomentumTracking()
        }
    }, [canEnableAvatarHoverPreview])

    const columnCountRef = useRef(0)
    const modelCountRef = useRef(0)
    const passiveMode = usePassive()

    useEffect(() => {
        modelCountRef.current = models.length
    }, [models])
    const windowWidth = useScreenStateSelector('windowWidth')
    const windowHeight = useScreenStateSelector('windowHeight')
    const hasPinnedRooms = useScreenStateSelector('hasPinnedRooms')
    const pinLimitReached = useScreenStateSelector('pinLimitReached')
    const singlePin = useScreenStateSelector('singlePin')

    const isMobileDevice =
        (isMobile || isIOS || isIpad) &&
        !(
            windowWidth > APP_BREAKPOINTS.lg / 2 &&
            windowHeight > APP_BREAKPOINTS.lg / 2
        )

    // Worker list subscriptions are reference-counted and shared with other
    // consumers (the sidebar reads MODELS_YOU_MAY_LIKE through useList), so
    // release only the subscriptions this hook took.
    useEffect(() => {
        if (pinTrendingRooms !== 1) return
        subscribeWorkerListUpdates(ListType.PINNED_MODEL_TRENDING)
        return () =>
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_TRENDING)
    }, [pinTrendingRooms])

    useEffect(() => {
        if (pinPopularRooms !== 1) return
        subscribeWorkerListUpdates(ListType.PINNED_MODEL_POPULAR)
        return () => unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_POPULAR)
    }, [pinPopularRooms])

    useEffect(() => {
        if (pinRecentlyVisitedRooms !== 1) return
        subscribeWorkerListUpdates(ListType.PINNED_MODEL_RECENTLY_VISITED)
        return () =>
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_RECENTLY_VISITED)
    }, [pinRecentlyVisitedRooms])

    useEffect(() => {
        if (pinMissMFC !== 1) return
        subscribeWorkerListUpdates(ListType.PINNED_MODEL_MISS_MFC)
        return () =>
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_MISS_MFC)
    }, [pinMissMFC])

    useEffect(() => {
        if (pinModelsYouMayLike !== 1) return
        subscribeWorkerListUpdates(ListType.MODELS_YOU_MAY_LIKE)
        subscribeWorkerListUpdates(ListType.PINNED_MODELS_YOU_MAY_LIKE)
        return () => {
            unsubscribeWorkerListUpdates(ListType.MODELS_YOU_MAY_LIKE)
            unsubscribeWorkerListUpdates(ListType.PINNED_MODELS_YOU_MAY_LIKE)
        }
    }, [pinModelsYouMayLike])

    const getCenteredIndex = useMemo(
        () =>
            throttle(
                ({
                    averageRowHeight,
                    totalRowsHeight,
                    containerHeight,
                    scrollOffset,
                }) => {
                    const index = Math.ceil(scrollOffset / averageRowHeight)

                    if (totalRowsHeight < scrollOffset + containerHeight + 5) {
                        setCenteredIndexState(index + 1)
                        return
                    }

                    setCenteredIndexState(index)
                },
                120,
            ),
        [],
    )

    useEffect(() => {
        return () => {
            getCenteredIndex.cancel()
        }
    }, [getCenteredIndex])

    const enableCenteredIndexTracking =
        gridDensity === GridDensityUeopt.large &&
        (display === DisplayUeopt.detailed ||
            display === DisplayUeopt.preview) &&
        isMobileDevice &&
        !hasPinnedRooms

    useEffect(() => {
        resetCenteredIndexState()
    }, [enableCenteredIndexTracking])

    const enablePinToTopSymbols =
        pinRecentlyVisitedRooms === 1 ||
        pinTrendingRooms === 1 ||
        pinPopularRooms === 1 ||
        pinNewModels === 1 ||
        pinMissMFC === 1 ||
        pinModelsYouMayLike === 1 ||
        sort === 'miss_mfc'

    const onColumnCount = useCallback((columnCount: number) => {
        columnCountRef.current = columnCount
    }, [])

    const onPreviewScroll = useMemo(() => {
        if (!previewScrollEnabled) {
            return null
        }

        // maps a scroll position to the tile it lands on — shared by the
        // preview (settled scroll) and the scroll cursor (every scroll) so
        // the cursor's sweep always ends on the tile that will preview.
        // Returns null while the grid is unmeasured (leave the selection
        // alone) and { null, null } when nothing should be selected.
        const resolveScrollTile = ({
            averageRowHeight,
            scrollOffset,
            rowCount,
        }): ScrollTile | null => {
            if (scrollOffset < 5) {
                return { column: null, row: null }
            }

            const columnCount = columnCountRef.current
            if (!columnCount || !averageRowHeight) {
                return null
            }

            const offsetRatio =
                (scrollOffset + getPreviewTriggerOffset(scrollOffset)) /
                averageRowHeight

            const row = Math.max(
                Math.min(Math.ceil(offsetRatio), rowCount - 1),
                1,
            )

            // row 0 is the banner (always present in preview mode), so row
            // holds models[(row - 1) * columnCount] onwards — the last row
            // may be partial, or empty when there are no models at all
            const itemsInRow = Math.min(
                modelCountRef.current - (row - 1) * columnCount,
                columnCount,
            )

            if (itemsInRow < 1) {
                return { column: null, row: null }
            }

            // fractional progress through the row's scroll span picks the
            // column, so the selection walks each row's tiles left to right
            // before wrapping to the next row as the user scrolls down
            const column = Math.min(
                Math.floor((offsetRatio % 1) * columnCount),
                itemsInRow - 1,
            )

            return { column, row }
        }

        const selectPreview = (props) => {
            const tile = resolveScrollTile(props)

            if (!tile) {
                return
            }

            setPreviewOnScrollIndexState(tile.row, tile.column)
        }

        // the cursor has no velocity gate: it is the thing that keeps the
        // left-to-right order visible while the preview is held back
        const cursorPass = throttle((props) => {
            const tile = resolveScrollTile(props)

            if (!tile) {
                return
            }

            setScrollCursorTargetState(
                tile.row,
                tile.column,
                columnCountRef.current,
            )
        }, SCROLL_CURSOR_THROTTLE_MS)

        const velocityPass = throttle((props) => {
            const currentTime = performance.now()
            const timeDelta = currentTime - previewScrollPreviousTimeRef.current
            const distanceDelta =
                props.scrollOffset - previewScrollPreviousRef.current

            previewScrollPreviousRef.current = props.scrollOffset
            previewScrollPreviousTimeRef.current = currentTime

            const velocity =
                timeDelta > 0 ? Math.abs(distanceDelta / timeDelta) : 0

            // hide previews while flinging; stay hidden until the scroll
            // settles to a crawl
            if (velocity > 1) {
                previewScrollDisabledRef.current = true
                resetPreviewOnScrollIndexState()
                return
            }

            if (velocity > 0.1 && previewScrollDisabledRef.current) {
                return
            }

            previewScrollDisabledRef.current = false

            selectPreview(props)
        }, 120)

        // the velocity sampling can end a fling still disabled (no further
        // scroll events fire to clear it), so always run a final pass once
        // scrolling stops
        const settlePass = debounce((props) => {
            previewScrollDisabledRef.current = false
            selectPreview(props)
        }, 180)

        return Object.assign(
            (props) => {
                cursorPass(props)
                velocityPass(props)
                settlePass(props)
            },
            {
                cancel: () => {
                    cursorPass.cancel()
                    velocityPass.cancel()
                    settlePass.cancel()
                },
            },
        )
    }, [previewScrollEnabled])

    useEffect(() => {
        if (!onPreviewScroll) {
            return
        }

        return () => {
            onPreviewScroll.cancel()
            previewScrollDisabledRef.current = false
            resetPreviewOnScrollIndexState()
            resetScrollCursorState()
        }
    }, [onPreviewScroll])

    //
    // Marc: Code to make the the rows automatically display the preview on the
    // next row every 7 seconds. Be sure to comment out the "previewRowIndexRef"
    // and "setMaxPreviewRowIndex" variables above to make it loop back...
    //
    // Marc (sometime later): If using this, it needs to be updated because
    // the API has changed. Basically have to include the column and the auto
    // rotation of the column.
    //
    // useEffect(() => {
    //     if (display !== DisplayUeopt.default) {
    //         return
    //     }

    //     previewRowIndexRef.current = setTimeout(() => {
    //         setPreviewIndex((index) => {
    //             return (index + 1) % maxPreviewRowIndex
    //         })
    //     }, 7000)

    //     return () => {
    //         clearTimeout(previewRowIndexRef.current)
    //     }
    // }, [previewIndex?.row, maxPreviewRowIndex])

    return {
        // Shows up automatically:
        // showCrownBadge: boolean = pinPastWinners === 1,
        enableOnScroll: previewScrollEnabled || enableCenteredIndexTracking,
        displayNoteIconButton: pinModelsWithNotes === 1,
        displayRecentlyVisitedHighlight: pinRecentlyVisitedRooms === 1,
        displayTrendingHighlight: pinTrendingRooms === 1,
        displayPopularHighlight: pinPopularRooms === 1,
        displayRecentlyVisitedBadge: pinRecentlyVisitedRooms === 1,
        displayRecommendationHighlight: pinModelsYouMayLike === 1,
        displayRecommendationBadge: pinModelsYouMayLike === 1,
        displayTrendingBadge: pinTrendingRooms === 1,
        displayPopularBadge: pinPopularRooms === 1,
        displayNewModelBadge: pinNewModels === 1,
        displayMissMFCBadge: sort === 'miss_mfc' || pinMissMFC === 1,
        onScroll: onPreviewScroll || getCenteredIndex,
        // roomHeight: estimatedRoomHeight,
        // roomWidth: estimatedRoomWidth,
        enableCenteredIndexTracking,
        isMobile: isMobileDevice,
        enablePinToTopSymbols,
        // windowWidth: width,
        // showPlaceholder,
        noModelsVisible,
        modelsLoaded,
        pinLimitReached,
        // pinnedRooms,
        onColumnCount,
        passiveMode,
        singlePin,
        // showChat,
        // vertical,
        // leftPin,
        models,
    }
}
